import {
  ActionIcon,
  Box,
  Button,
  FileButton,
  Group,
  Text,
  Textarea,
  Tooltip,
} from '@mantine/core';
import { IconAlertTriangle, IconPaperclip, IconSend, IconX } from '@tabler/icons-react';
import type { Communication } from '@medplum/fhirtypes';
import { notifications } from '@mantine/notifications';
import { useRef, useState } from 'react';
import {
  getMessageTextContent,
  getMessageAttachmentSummary,
  MESSAGING_MAX_ATTACHMENTS_PER_MESSAGE,
  validateMessagingAttachmentFile,
} from '../services/messagingFhir';
import classes from './ComposeBar.module.css';

interface ComposeBarProps {
  threadId: string;
  sending: boolean;
  onSend: (content: string, attachmentFiles: File[]) => Promise<void>;
  isExternalRecipient?: boolean;
  isClinicalChannel?: boolean;
  /** When set, show quoted message above input (quote reply) */
  replyingTo?: Communication | null;
  onCancelReply?: () => void;
}

export function ComposeBar({
  threadId,
  sending,
  onSend,
  isExternalRecipient = false,
  isClinicalChannel = false,
  replyingTo = null,
  onCancelReply,
}: ComposeBarProps): JSX.Element {
  // Thread context not used yet (reserved for API wiring).
  void threadId;

  const [content, setContent] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleSend = async (): Promise<void> => {
    if (!content.trim() && attachments.length === 0) return;

    await onSend(content, attachments);
    setContent('');
    setAttachments([]);
    // Refocus input for rapid follow-up messages
    setTimeout(() => textareaRef.current?.focus(), 0);
  };

  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const removeAttachment = (index: number): void => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleFilesSelected = (files: File[]): void => {
    const accepted: File[] = [];
    for (const file of files) {
      try {
        validateMessagingAttachmentFile(file);
        accepted.push(file);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'File could not be attached';
        notifications.show({ color: 'red', title: 'Attachment rejected', message });
      }
    }
    if (accepted.length === 0) return;
    setAttachments((prev) => {
      const combined = [...prev, ...accepted];
      if (combined.length > MESSAGING_MAX_ATTACHMENTS_PER_MESSAGE) {
        notifications.show({
          color: 'red',
          title: 'Too many attachments',
          message: `You can attach up to ${MESSAGING_MAX_ATTACHMENTS_PER_MESSAGE} files per message.`,
        });
        return combined.slice(0, MESSAGING_MAX_ATTACHMENTS_PER_MESSAGE);
      }
      return combined;
    });
  };

  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragOver = (e: React.DragEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFilesSelected(files);
    }
  };

  const isDisabled = sending || (!content.trim() && attachments.length === 0);

  const quotedSender = replyingTo?.sender?.display ?? 'Unknown';
  const quotedContentDisplay = replyingTo
    ? getMessageTextContent(replyingTo) || getMessageAttachmentSummary(replyingTo) || ''
    : '';

  return (
    <Box
      className={classes.composeBar}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      style={isDragOver ? { borderColor: 'var(--mantine-color-gold-5)', backgroundColor: 'var(--mantine-color-gold-0)' } : undefined}
    >
      {/* PHI Warning for external recipients or clinical channels */}
      {(isExternalRecipient || isClinicalChannel) && (
        <Box className={classes.phiWarning}>
          <IconAlertTriangle size={16} color="var(--mantine-color-yellow-7)" />
          <Text size="xs" c="yellow.8">
            {isExternalRecipient
              ? 'You are sending to an external recipient. Ensure no PHI is included unless authorized.'
              : 'This is a clinical conversation. Messages may contain protected health information (PHI).'}
          </Text>
        </Box>
      )}

      {/* Quote reply preview */}
      {replyingTo && onCancelReply && (
        <Box className={classes.quotePreview}>
          <Box style={{ flex: 1, minWidth: 0 }}>
            <Text size="xs" fw={500} c="dimmed" mb={2}>
              Replying to {quotedSender}
            </Text>
            <Text size="xs" c="dimmed" lineClamp={2}>
              {quotedContentDisplay}
            </Text>
          </Box>
          <Tooltip label="Cancel reply">
            <ActionIcon size="sm" variant="subtle" onClick={onCancelReply} aria-label="Cancel reply">
              <IconX size={14} />
            </ActionIcon>
          </Tooltip>
        </Box>
      )}

      {/* Attachment previews */}
      {attachments.length > 0 && (
        <Group gap="xs" mb="sm" wrap="wrap">
          {attachments.map((file, idx) => (
            <Box key={idx} className={classes.attachmentPreview}>
              <Text size="xs" truncate style={{ maxWidth: 100 }}>
                {file.name}
              </Text>
              <ActionIcon size="xs" variant="subtle" onClick={() => removeAttachment(idx)} aria-label="Remove attachment">
                <IconX size={12} />
              </ActionIcon>
            </Box>
          ))}
        </Group>
      )}

      {/* Input row */}
      <Group gap="sm" align="flex-end">
        {/* Attachment button */}
        <FileButton onChange={handleFilesSelected} accept="image/*,application/pdf,.doc,.docx" multiple>
          {(props) => (
            <Tooltip label="Attach files">
              <ActionIcon {...props} variant="subtle" size="lg" aria-label="Attach files">
                <IconPaperclip size={20} />
              </ActionIcon>
            </Tooltip>
          )}
        </FileButton>

        {/* Text input */}
        <Textarea
          ref={textareaRef}
          placeholder="Type a message..."
          value={content}
          onChange={(e) => setContent(e.target.value)}
          onKeyDown={handleKeyDown}
          autosize
          minRows={1}
          maxRows={4}
          style={{ flex: 1 }}
          disabled={sending}
          classNames={{ input: classes.textareaInput }}
        />

        {/* Send button */}
        <Button
          onClick={handleSend}
          disabled={isDisabled}
          loading={sending}
          color="unifyGold"
          leftSection={!sending && <IconSend size={18} />}
          aria-label="Send message"
        >
          Send
        </Button>
      </Group>
    </Box>
  );
}
